import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import './TestResultsPage.css';
import axios from 'axios';

// Настройка API
const API_URL = 'https://statopros2025.paragraf.spb.ru';

// Хардкод для предустановленных опросов
const PRESET_POLLS = [
  {
    id: 'aac95574-0a9b-4f0a-a9c1-d14b586d5f17',
    title: 'Опрос родителей',
    responses_count: 0
  },
  {
    id: 'c0099f23-ac9d-47d6-a246-5d69c0acdd41',
    title: 'Опрос детей',
    responses_count: 0
  }
];

// Создаем экземпляр axios с базовыми настройками
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Добавляем перехватчик для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

// Вспомогательные функции для работы с текстом
const textHelpers = {
  // Обрезает текст до указанной длины с многоточием
  truncateText: (text, maxLength = 30) => {
    if (!text || typeof text !== 'string') return text || '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  },
  
  // Определяет размер шрифта в зависимости от длины текста
  getFontSize: (text, defaultSize = 14) => {
    if (!text) return defaultSize;
    const length = text.length;
    if (length > 50) return defaultSize - 4;
    if (length > 30) return defaultSize - 2;
    return defaultSize;
  },
  
  // Разбивает длинный текст на несколько строк
  wrapText: (text, maxCharsPerLine = 20) => {
    if (!text || typeof text !== 'string') return [text || ''];
    
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + ' ' + word).length > maxCharsPerLine && currentLine.length > 0) {
        lines.push(currentLine.trim());
        currentLine = word;
      } else {
        currentLine += (currentLine ? ' ' : '') + word;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine.trim());
    }
    
    return lines;
  }
};

// Компонент статистики по вопросам
const QuestionStats = ({ analytics, questionIndex, questionText, chartType: propChartType }) => {
  const [chartData, setChartData] = useState([]);
  const [colors, setColors] = useState([]);
  const [isRatingQuestion, setIsRatingQuestion] = useState(false);
  const [averageRating, setAverageRating] = useState(null);
  const [chartType, setChartType] = useState(propChartType || 'auto');
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [hoveredTooltip, setHoveredTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const tooltipTimeoutRef = useRef(null);

  useEffect(() => {
    if (!analytics || !analytics.options || !Array.isArray(analytics.options)) {
      setChartData([]);
      return;
    }

    // Фильтруем нулевые ответы (пропуски) из отображения
    const filteredData = analytics.options.filter(option => option.value !== '0');
    
    const data = filteredData.map(option => ({
      name: option.value,
      value: option.count,
      percentage: option.percentage
    }));
    
    setChartData(data);
    
    // Определяем тип вопроса
    const ratingPattern = /^[1-5]$|^[1-9]$|^10$|^\d+$/;
    const allNumericOptions = filteredData.every(opt => 
      ratingPattern.test(opt.value?.trim())
    );
    
    setIsRatingQuestion(allNumericOptions);
    
    // Рассчитываем среднюю оценку для рейтинговых вопросов
    if (allNumericOptions) {
      const ratingValues = filteredData.map(opt => ({
        rating: parseInt(opt.value) || 0,
        count: opt.count || 0
      }));
      
      const totalRating = ratingValues.reduce((sum, item) => sum + (item.rating * item.count), 0);
      const totalCount = ratingValues.reduce((sum, item) => sum + item.count, 0);
      
      if (totalCount > 0) {
        setAverageRating((totalRating / totalCount).toFixed(1));
      } else {
        setAverageRating(null);
      }
      
      // Для рейтинговых вопросов сортируем по возрастанию
      data.sort((a, b) => {
        const aNum = parseInt(a.name) || 0;
        const bNum = parseInt(b.name) || 0;
        return aNum - bNum;
      });
      setChartData(data);
    }
    
    // Определяем количество уникальных вариантов для автоматического выбора типа графика
    const uniqueOptions = data.length;
    if (propChartType === 'auto') {
      if (allNumericOptions && uniqueOptions <= 10) {
        setChartType('rating');
      } else if (uniqueOptions <= 5) {
        setChartType('donut');
      } else if (uniqueOptions <= 8) {
        setChartType('radar');
      } else if (uniqueOptions <= 15) {
        setChartType('vertical');
      } else if (uniqueOptions <= 20) {
        setChartType('horizontal');
      } else {
        setChartType('table');
      }
    }
    
    // Генерация цветов в синей гамме с добавлением дополнительных цветов для круговой диаграммы
    const colorPalette = [
      '#0f6cbd', '#1a7fd8', '#2d8fe6', '#4aa1f0', '#6bb3f5',
      '#8dc6f9', '#b0d9fc', '#d4ebff', '#0a5ca6', '#084d8c',
      '#063e73', '#042f59', '#22c3aa', '#2dd4bf', '#5eead4',
      '#f28b82', '#fbbc04', '#34a853', '#ea4335', '#ff6d01',
      '#46bdc6', '#7b1fa2', '#af52de', '#00bcd4', '#8bc34a'
    ];
    setColors(colorPalette);
  }, [analytics, propChartType]);

  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  const toggleShowAllOptions = () => {
    setShowAllOptions(!showAllOptions);
  };

  const showTooltip = (text, event) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    setHoveredTooltip({
      show: true,
      text: text,
      x: event.clientX + 10,
      y: event.clientY + 10
    });
  };

  const hideTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    tooltipTimeoutRef.current = setTimeout(() => {
      setHoveredTooltip({ show: false, text: '', x: 0, y: 0 });
    }, 100);
  };

  // Убираем таймаут при размонтировании
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  if (!analytics || analytics.analyticsType === 'error') {
    return (
      <div className="analytics-card error">
        <div className="card-header">
          <div className="question-info">
            <span className="question-number">Вопрос {questionIndex + 1}</span>
            <h3 className="question-title">{questionText || `Вопрос ${questionIndex + 1}`}</h3>
          </div>
          <span className="error-indicator">Ошибка загрузки</span>
        </div>
        <div className="card-content">
          <div className="error-state">
            <div className="error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#dc3545"/>
              </svg>
            </div>
            <div className="error-message">
              {analytics?.error || 'Не удалось загрузить данные для этого вопроса'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalAnswers = analytics.totalAnswers || 0;
  const zeroAnswers = analytics.options?.find(opt => opt.value === '0')?.count || 0;
  const answeredCount = totalAnswers - zeroAnswers;
  const displayData = showAllOptions ? chartData : chartData.slice(0, 15);
  const hasMoreOptions = chartData.length > 15;

  // Круговая диаграмма (Donut Chart)
  const renderDonutChart = () => {
    if (displayData.length === 0) return null;
    
    // Сортировка по убыванию для лучшего визуального восприятия
    const sortedData = [...displayData].sort((a, b) => b.value - a.value);
    const totalValue = sortedData.reduce((sum, item) => sum + item.value, 0);
    
    // Параметры диаграммы
    const centerX = 180;
    const centerY = 180;
    const radius = 140;
    const innerRadius = 80;
    const holeRadius = 60;
    
    // Рассчитываем сегменты
    let currentAngle = -Math.PI / 2;
    const segments = [];
    
    sortedData.forEach((item, index) => {
      const angle = (item.value / totalValue) * 2 * Math.PI;
      const endAngle = currentAngle + angle;
      
      // Координаты для внешней дуги
      const startX = centerX + radius * Math.cos(currentAngle);
      const startY = centerY + radius * Math.sin(currentAngle);
      const endX = centerX + radius * Math.cos(endAngle);
      const endY = centerY + radius * Math.sin(endAngle);
      
      // Координаты для внутренней дуги
      const innerStartX = centerX + innerRadius * Math.cos(currentAngle);
      const innerStartY = centerY + innerRadius * Math.sin(currentAngle);
      const innerEndX = centerX + innerRadius * Math.cos(endAngle);
      const innerEndY = centerY + innerRadius * Math.sin(endAngle);
      
      // Создаем path для сегмента
      const largeArcFlag = angle > Math.PI ? 1 : 0;
      
      const pathData = `
        M ${innerStartX} ${innerStartY}
        L ${startX} ${startY}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
        L ${innerEndX} ${innerEndY}
        A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}
        Z
      `;
      
      // Координаты для выноски (если процент больше 5%)
      let calloutX, calloutY;
      if (item.percentage > 5) {
        const midAngle = currentAngle + angle / 2;
        const calloutRadius = radius + 30;
        calloutX = centerX + calloutRadius * Math.cos(midAngle);
        calloutY = centerY + calloutRadius * Math.sin(midAngle);
      }
      
      segments.push({
        index,
        item,
        pathData,
        color: colors[index % colors.length],
        startAngle: currentAngle,
        endAngle,
        midAngle: currentAngle + angle / 2,
        calloutX,
        calloutY,
        percentage: item.percentage || (item.value / totalValue * 100)
      });
      
      currentAngle = endAngle;
    });
    
    // Координаты для центрального текста
    const centerText = sortedData.length <= 8 ? 'Всего ответов' : 'Ответов';
    
    return (
      <div className="chart-donut">
        <div className="donut-container">
          <svg className="donut-svg" viewBox="0 0 360 360" width="360" height="360">
            {/* Сегменты диаграммы */}
            {segments.map(segment => (
              <g key={segment.index}>
                <path
                  d={segment.pathData}
                  fill={segment.color}
                  className="donut-segment"
                  onMouseEnter={() => setHoveredSlice(segment.index)}
                  onMouseLeave={() => setHoveredSlice(null)}
                  style={{
                    opacity: hoveredSlice === null || hoveredSlice === segment.index ? 1 : 0.7,
                    filter: hoveredSlice === segment.index ? 'drop-shadow(0 0 4px rgba(0,0,0,0.3))' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                />
                
                {/* Выноски для значительных сегментов */}
                {segment.calloutX && segment.calloutY && (
                  <g className="callout">
                    <line
                      x1={centerX + radius * Math.cos(segment.midAngle)}
                      y1={centerY + radius * Math.sin(segment.midAngle)}
                      x2={segment.calloutX}
                      y2={segment.calloutY}
                      stroke="#666"
                      strokeWidth="1"
                      strokeDasharray="3,3"
                    />
                    
                    <rect
                      x={segment.calloutX - 40}
                      y={segment.calloutY - 20}
                      width={80}
                      height={40}
                      rx="8"
                      ry="8"
                      fill="white"
                      stroke="#ddd"
                      strokeWidth="1"
                    />
                    
                    <text
                      x={segment.calloutX}
                      y={segment.calloutY - 5}
                      textAnchor="middle"
                      className="callout-percentage"
                      fontWeight="bold"
                      fontSize="11"
                    >
                      {segment.percentage.toFixed(1)}%
                    </text>
                    
                    <text
                      x={segment.calloutX}
                      y={segment.calloutY + 12}
                      textAnchor="middle"
                      className="callout-label"
                      fontSize="9"
                    >
                      {textHelpers.truncateText(segment.item.name, 12)}
                    </text>
                  </g>
                )}
              </g>
            ))}
            
            {/* Внутренний круг (дырка бублика) */}
            <circle
              cx={centerX}
              cy={centerY}
              r={holeRadius}
              fill="white"
            />
            
            {/* Центральный текст */}
            <text
              x={centerX}
              y={centerY - 10}
              textAnchor="middle"
              className="donut-center-text"
              fontWeight="bold"
              fontSize="16"
            >
              {centerText}
            </text>
            
            <text
              x={centerX}
              y={centerY + 15}
              textAnchor="middle"
              className="donut-center-total"
              fontSize="24"
              fontWeight="bold"
              fill="#0f6cbd"
            >
              {totalValue}
            </text>
            
            {/* Вспомогательные линии и точки при наведении */}
            {hoveredSlice !== null && (
              <g>
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={innerRadius + 10}
                  fill="none"
                  stroke="#0f6cbd"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                />
                
                <text
                  x={centerX}
                  y={centerY + 50}
                  textAnchor="middle"
                  className="hover-info"
                  fontSize="11"
                  fill="#666"
                >
                  {textHelpers.truncateText(sortedData[hoveredSlice]?.name, 25)}: {sortedData[hoveredSlice]?.value}
                </text>
              </g>
            )}
          </svg>
        </div>
        
        <div className="donut-legend scrollable">
          {sortedData.map((item, index) => (
            <div 
              key={index} 
              className={`legend-item ${hoveredSlice === index ? 'highlighted' : ''}`}
              onMouseEnter={() => setHoveredSlice(index)}
              onMouseLeave={() => setHoveredSlice(null)}
              style={{
                backgroundColor: hoveredSlice === index ? '#f8f9fa' : 'transparent',
                borderLeft: `4px solid ${colors[index % colors.length]}`
              }}
            >
              <div className="legend-color" style={{ backgroundColor: colors[index % colors.length] }} />
              <div className="legend-text">
                <span 
                  className="legend-label"
                  onMouseEnter={(e) => showTooltip(item.name, e)}
                  onMouseLeave={hideTooltip}
                >
                  {textHelpers.truncateText(item.name, 40)}
                </span>
                <div className="legend-details">
                  <span className="legend-value">{item.value} ответов</span>
                  <span className="legend-percentage">{item.percentage?.toFixed(1) || '0.0'}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Паутинная диаграмма (для небольшого количества вариантов)
  const renderRadarChart = () => {
    if (displayData.length === 0) return null;
    
    const maxValue = Math.max(...displayData.map(item => item.value));
    const centerX = 150;
    const centerY = 150;
    const radius = 120;
    const angleStep = (2 * Math.PI) / displayData.length;
    
    // Рассчитываем координаты точек
    const points = displayData.map((item, index) => {
      const angle = index * angleStep - Math.PI / 2;
      const valueRatio = maxValue > 0 ? item.value / maxValue : 0;
      const pointRadius = radius * valueRatio;
      
      return {
        x: centerX + pointRadius * Math.cos(angle),
        y: centerY + pointRadius * Math.sin(angle),
        labelX: centerX + (radius + 20) * Math.cos(angle),
        labelY: centerY + (radius + 20) * Math.sin(angle),
        name: item.name,
        value: item.value,
        percentage: item.percentage
      };
    });
    
    // Создаем path для заливки области
    const pathData = points.map((point, index) => {
      return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }).join(' ') + ' Z';
    
    // Создаем линии сетки
    const gridCircles = [];
    for (let i = 1; i <= 4; i++) {
      const gridRadius = radius * (i / 4);
      gridCircles.push(
        <circle
          key={`grid-${i}`}
          cx={centerX}
          cy={centerY}
          r={gridRadius}
          className="radar-grid"
        />
      );
    }
    
    // Создаем оси
    const axes = [];
    for (let i = 0; i < displayData.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const x2 = centerX + radius * Math.cos(angle);
      const y2 = centerY + radius * Math.sin(angle);
      
      axes.push(
        <line
          key={`axis-${i}`}
          x1={centerX}
          y1={centerY}
          x2={x2}
          y2={y2}
          className="radar-axis"
        />
      );
    }
    
    return (
      <div className="chart-radar">
        <div className="radar-container">
          <svg className="radar-svg" viewBox="0 0 300 300">
            {gridCircles}
            {axes}
            <path
              d={pathData}
              className="radar-area"
            />
            {points.map((point, index) => (
              <circle
                key={`point-${index}`}
                cx={point.x}
                cy={point.y}
                r="4"
                className="radar-point"
                fill={colors[index % colors.length]}
              />
            ))}
            {points.map((point, index) => (
              <g key={`label-${index}`}>
                <title>{point.name}</title>
                <text
                  x={point.labelX}
                  y={point.labelY}
                  className="radar-label"
                  fontSize={textHelpers.getFontSize(point.name, 10)}
                  transform={`rotate(${index * (360 / displayData.length)}, ${point.labelX}, ${point.labelY})`}
                  onMouseEnter={(e) => showTooltip(point.name, e)}
                  onMouseLeave={hideTooltip}
                >
                  {textHelpers.truncateText(point.name, 15)}
                </text>
              </g>
            ))}
          </svg>
        </div>
        <div className="radar-legend scrollable">
          {displayData.map((item, index) => (
            <div 
              key={index} 
              className="legend-item"
              onMouseEnter={(e) => showTooltip(item.name, e)}
              onMouseLeave={hideTooltip}
            >
              <div className="legend-color" style={{ backgroundColor: colors[index % colors.length] }} />
              <div className="legend-text">
                <span className="legend-label">{textHelpers.truncateText(item.name, 30)}</span>
                <span className="legend-value">{item.value} ({item.percentage?.toFixed(1)}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Диаграмма для рейтингов (горизонтальные бары с цифрами)
  const renderRatingChart = () => {
    if (displayData.length === 0) return null;
    
    const maxValue = Math.max(...displayData.map(item => item.value));
    
    return (
      <div className="chart-rating">
        <div className="rating-scale scrollable">
          {displayData.map((item, index) => {
            const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            
            return (
              <div key={index} className="rating-item">
                <div className="rating-label">
                  <span 
                    className="rating-number"
                    onMouseEnter={(e) => showTooltip(item.name, e)}
                    onMouseLeave={hideTooltip}
                  >
                    {textHelpers.truncateText(item.name, 25)}
                  </span>
                  <span className="rating-count">{item.value}</span>
                </div>
                <div className="rating-bar">
                  <div 
                    className="bar-fill"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: colors[index % colors.length]
                    }}
                  >
                    <span className="bar-percentage">{item.percentage?.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Вертикальная гистограмма (столбцы идут снизу вверх)
  const renderVerticalChart = () => {
    if (displayData.length === 0) return null;
    
    const maxValue = Math.max(...displayData.map(item => item.value));
    const containerHeight = 300;
    
    return (
      <div className="chart-vertical">
        <div className="bars-container scrollable">
          {displayData.map((item, index) => {
            const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            const barHeight = (percentage / 100) * (containerHeight - 50);
            const fontSize = textHelpers.getFontSize(item.name, 12);
            
            return (
              <div key={index} className="bar-column">
                <div className="bar-value">{item.value}</div>
                <div 
                  className="bar"
                  style={{
                    height: `${barHeight}px`,
                    backgroundColor: colors[index % colors.length]
                  }}
                />
                <div 
                  className="bar-label"
                  onMouseEnter={(e) => showTooltip(item.name, e)}
                  onMouseLeave={hideTooltip}
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {textHelpers.truncateText(item.name, 20)}
                </div>
                <div className="bar-percent">{item.percentage?.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Горизонтальная диаграмма
  const renderHorizontalChart = () => {
    if (displayData.length === 0) return null;
    
    const maxValue = Math.max(...displayData.map(item => item.value));
    
    return (
      <div className="chart-horizontal">
        <div className="bars-list scrollable">
          {displayData.map((item, index) => {
            const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            const fontSize = textHelpers.getFontSize(item.name, 14);
            
            return (
              <div key={index} className="bar-row">
                <div 
                  className="row-label"
                  onMouseEnter={(e) => showTooltip(item.name, e)}
                  onMouseLeave={hideTooltip}
                >
                  <div className="color-dot" style={{ backgroundColor: colors[index % colors.length] }} />
                  <span 
                    className="label-text"
                    style={{ fontSize: `${fontSize}px` }}
                  >
                    {textHelpers.truncateText(item.name, 40)}
                  </span>
                </div>
                <div className="row-bar">
                  <div 
                    className="bar"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: colors[index % colors.length]
                    }}
                  >
                    <span className="bar-text">{item.value}</span>
                  </div>
                </div>
                <div className="row-percent">{item.percentage?.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Компактная таблица
  const renderTable = () => {
    if (displayData.length === 0) return null;
    
    return (
      <div className="chart-table">
        <div className="table-container scrollable">
          <table>
            <thead>
              <tr>
                <th className="text-left">Ответ</th>
                <th className="text-center">Количество</th>
                <th className="text-center">Процент</th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((item, index) => (
                <tr key={index}>
                  <td className="text-left">
                    <div 
                      className="table-cell"
                      onMouseEnter={(e) => showTooltip(item.name, e)}
                      onMouseLeave={hideTooltip}
                    >
                      <div className="cell-color" style={{ backgroundColor: colors[index % colors.length] }} />
                      <span className="cell-text">{textHelpers.truncateText(item.name, 50)}</span>
                    </div>
                  </td>
                  <td className="text-center">
                    <span className="cell-number">{item.value}</span>
                  </td>
                  <td className="text-center">
                    <span className="cell-percent">{item.percentage?.toFixed(1)}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Выбор типа графика
  const renderChart = () => {
    switch (chartType) {
      case 'rating':
        return renderRatingChart();
      case 'donut':
        return renderDonutChart();
      case 'radar':
        return renderRadarChart();
      case 'vertical':
        return renderVerticalChart();
      case 'horizontal':
        return renderHorizontalChart();
      case 'table':
        return renderTable();
      default:
        return renderHorizontalChart();
    }
  };

  return (
    <div className="analytics-card">
      {hoveredTooltip.show && (
        <div 
          className="chart-tooltip"
          style={{
            position: 'fixed',
            left: hoveredTooltip.x,
            top: hoveredTooltip.y,
            zIndex: 1000
          }}
        >
          {hoveredTooltip.text}
        </div>
      )}
      
      <div className="card-header">
        <div className="question-info">
          <span className="question-number">Вопрос {questionIndex + 1}</span>
          <h3 
            className="question-title"
            onMouseEnter={(e) => showTooltip(questionText, e)}
            onMouseLeave={hideTooltip}
          >
            {textHelpers.truncateText(questionText, 80)}
          </h3>
        </div>
        <div className="question-stats">
          <div className="stat-item">
            <span className="stat-label">Всего ответов</span>
            <span className="stat-value">{totalAnswers}</span>
          </div>
          {isRatingQuestion && averageRating && (
            <div className="stat-item highlight">
              <span className="stat-label">Средняя оценка</span>
              <span className="stat-value">{averageRating}</span>
            </div>
          )}
          <div className="stat-item">
            <span className="stat-label">Вариантов</span>
            <span className="stat-value">{chartData.length}</span>
          </div>
        </div>
      </div>
      
      {/* Панель управления типом графика */}
      <div className="chart-controls">
        <div className="controls-label">Тип диаграммы:</div>
        <div className="chart-type-buttons">
          <button 
            className={`type-btn ${chartType === 'auto' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('auto')}
            title="Автоматический выбор"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="currentColor"/>
            </svg>
            Авто
          </button>
          <button 
            className={`type-btn ${chartType === 'donut' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('donut')}
            title="Круговая диаграмма с выносками"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
              <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
            Круговая
          </button>
          <button 
            className={`type-btn ${chartType === 'radar' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('radar')}
            title="Паутинная диаграмма"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="currentColor"/>
            </svg>
            Паутина
          </button>
          <button 
            className={`type-btn ${chartType === 'rating' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('rating')}
            title="Рейтинговая шкала"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill="currentColor"/>
            </svg>
            Рейтинг
          </button>
          <button 
            className={`type-btn ${chartType === 'vertical' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('vertical')}
            title="Вертикальная гистограмма"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor"/>
            </svg>
            Столбцы
          </button>
          <button 
            className={`type-btn ${chartType === 'horizontal' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('horizontal')}
            title="Горизонтальная диаграмма"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M4 9h16v2H4zM4 13h16v2H4z" fill="currentColor"/>
            </svg>
            Полосы
          </button>
          <button 
            className={`type-btn ${chartType === 'table' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('table')}
            title="Таблица данных"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M10 10.02h5V21h-5zM17 21h3c1.1 0 2-.9 2-2v-9h-5v11zm3-18H5c-1.1 0-2 .9-2 2v3h19V5c0-1.1-.9-2-2-2zM3 19c0 1.1.9 2 2 2h3V10H3v9z" fill="currentColor"/>
            </svg>
            Таблица
          </button>
        </div>
      </div>
      
      {zeroAnswers > 0 && (
        <div className="skipped-notice">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#6c757d"/>
          </svg>
          <span className="skipped-text">
            <strong>{zeroAnswers}</strong> респондентов пропустили этот вопрос 
            ({totalAnswers > 0 ? ((zeroAnswers / totalAnswers) * 100).toFixed(1) : 0}%)
          </span>
        </div>
      )}

      <div className="card-content">
        {displayData.length > 0 ? (
          <div className="chart-container">
            {renderChart()}
            {hasMoreOptions && !showAllOptions && (
              <div className="more-options">
                <button className="show-more-btn" onClick={toggleShowAllOptions}>
                  Показать все {chartData.length} вариантов
                </button>
              </div>
            )}
            {hasMoreOptions && showAllOptions && (
              <div className="more-options">
                <button className="show-less-btn" onClick={toggleShowAllOptions}>
                  Скрыть лишние варианты
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="#dee2e6"/>
              </svg>
            </div>
            <div className="empty-text">
              {totalAnswers > 0 ? 'Нет данных для отображения' : 'На этот вопрос еще нет ответов'}
            </div>
          </div>
        )}
      </div>
      
      <div className="card-footer">
        <div className="footer-info">
          {chartType === 'auto' && <span className="info-text">Автоматический выбор</span>}
          {chartType === 'donut' && <span className="info-text">Круговая диаграмма</span>}
          {chartType === 'radar' && <span className="info-text">Паутинная диаграмма</span>}
          {chartType === 'rating' && <span className="info-text">Рейтинговая шкала</span>}
          {chartType === 'vertical' && <span className="info-text">Гистограмма (снизу вверх)</span>}
          {chartType === 'horizontal' && <span className="info-text">Горизонтальная диаграмма</span>}
          {chartType === 'table' && <span className="info-text">Таблица данных</span>}
          <span className="info-divider">•</span>
          <span className="info-text">Всего ответов: {totalAnswers}</span>
          {hasMoreOptions && !showAllOptions && (
            <>
              <span className="info-divider">•</span>
              <span className="info-text">Показано: 15 из {chartData.length}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Основная функция OprosExport
export default function OprosExport() {
  const [activeTab, setActiveTab] = useState('parents'); // 'parents' или 'children'
  const [filters, setFilters] = useState({
    region: '',
    district: '',
    foodBlockId: ''
  });
  
  const [availableFilters, setAvailableFilters] = useState({
    regions: [],
    districts: [],
    foodBlocks: [],
    districtsByRegion: {}
  });
  
  const [pollData, setPollData] = useState({
    parents: {
      questions: [],
      analytics: null,
      summary: { total_responses: 0, filtered_responses: 0 },
      loading: false,
      error: null
    },
    children: {
      questions: [],
      analytics: null,
      summary: { total_responses: 0, filtered_responses: 0 },
      loading: false,
      error: null
    }
  });
  
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingAllAnalytics, setLoadingAllAnalytics] = useState(false);
  const [error, setError] = useState('');

  // Флаг для отслеживания монтирования
  const isMountedRef = useRef(false);

  // Загрузка доступных фильтров при монтировании - теперь стабильная функция
  const fetchAvailableFilters = useCallback(async () => {
    // Проверяем, смонтирован ли компонент
    if (!isMountedRef.current) return;
    
    try {
      setLoadingFilters(true);
      setError('');
      
      console.log('Загрузка фильтров...');
      
      // Пробуем загрузить фильтры из первого опроса (родителей)
      const response = await api.get(`/api/polls/${PRESET_POLLS[0].id}/basic-info`);
      
      console.log('Ответ от API:', response.data);
      
      if (response.data) {
        setAvailableFilters({
          regions: response.data.availableFilters?.regions || [],
          districts: response.data.availableFilters?.districts || [],
          foodBlocks: response.data.availableFilters?.foodBlocks || [],
          districtsByRegion: response.data.availableFilters?.districtsByRegion || {}
        });
      } else {
        console.warn('API вернул пустой ответ');
        setAvailableFilters({
          regions: [],
          districts: [],
          foodBlocks: [],
          districtsByRegion: {}
        });
      }
      
    } catch (err) {
      console.error('Ошибка загрузки фильтров:', err);
      
      // Более конкретное сообщение об ошибке
      let errorMessage = 'Ошибка загрузки фильтров: ';
      if (err.response) {
        errorMessage += `Сервер вернул статус ${err.response.status}`;
      } else if (err.request) {
        errorMessage += 'Нет ответа от сервера. Проверьте подключение к интернету.';
      } else {
        errorMessage += err.message || 'Неизвестная ошибка';
      }
      
      setError(errorMessage);
      
      setAvailableFilters({
        regions: [],
        districts: [],
        foodBlocks: [],
        districtsByRegion: {}
      });
    } finally {
      if (isMountedRef.current) {
        setLoadingFilters(false);
      }
    }
  }, []); // Пустой массив зависимостей - функция не будет пересоздаваться

  // Инициализация при монтировании
  useEffect(() => {
    isMountedRef.current = true;
    
    // Загружаем фильтры только один раз при монтировании
    fetchAvailableFilters();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchAvailableFilters]); // Только fetchAvailableFilters в зависимостях

  // Загрузка аналитики для ВСЕХ опросов одновременно
  const loadAllQuestionAnalytics = useCallback(async () => {
    try {
      setLoadingAllAnalytics(true);
      
      // Создаем параметры запроса
      const params = new URLSearchParams();
      if (filters.region) params.append('region', filters.region);
      if (filters.district) params.append('district', filters.district);
      if (filters.foodBlockId) params.append('foodBlockId', filters.foodBlockId);
      
      console.log(`Загрузка аналитики для всех опросов с параметрами:`, params.toString());
      
      // Загружаем данные для обоих опросов параллельно
      const [parentsResponse, childrenResponse] = await Promise.all([
        api.get(`/api/polls/${PRESET_POLLS[0].id}/question-analytics?${params}`),
        api.get(`/api/polls/${PRESET_POLLS[1].id}/question-analytics?${params}`)
      ]);
      
      // Обновляем данные для родителей
      if (parentsResponse.data && parentsResponse.data.questionAnalytics) {
        // Получаем общее количество ответов из первого вопроса (или вычисляем)
        const totalFilteredResponses = parentsResponse.data.questionAnalytics[0]?.totalAnswers || 0;
        
        setPollData(prev => ({
          ...prev,
          parents: {
            ...prev.parents,
            analytics: parentsResponse.data.questionAnalytics,
            summary: {
              ...prev.parents.summary,
              filtered_responses: totalFilteredResponses
            },
            loading: false,
            error: null
          }
        }));
      } else {
        console.warn('API не вернул данные аналитики для родителей');
        setPollData(prev => ({
          ...prev,
          parents: {
            ...prev.parents,
            analytics: [],
            loading: false,
            error: 'Нет данных для выбранных фильтров'
          }
        }));
      }
      
      // Обновляем данные для детей
      if (childrenResponse.data && childrenResponse.data.questionAnalytics) {
        // Получаем общее количество ответов из первого вопроса (или вычисляем)
        const totalFilteredResponses = childrenResponse.data.questionAnalytics[0]?.totalAnswers || 0;
        
        setPollData(prev => ({
          ...prev,
          children: {
            ...prev.children,
            analytics: childrenResponse.data.questionAnalytics,
            summary: {
              ...prev.children.summary,
              filtered_responses: totalFilteredResponses
            },
            loading: false,
            error: null
          }
        }));
      } else {
        console.warn('API не вернул данные аналитики для детей');
        setPollData(prev => ({
          ...prev,
          children: {
            ...prev.children,
            analytics: [],
            loading: false,
            error: 'Нет данных для выбранных фильтров'
          }
        }));
      }
      
    } catch (err) {
      console.error('Ошибка загрузки аналитики:', err);
      
      // Устанавливаем ошибки для обоих опросов
      const errorMessage = `Ошибка загрузки аналитики вопросов: ${err.message || 'Проверьте подключение'}`;
      
      setPollData(prev => ({
        parents: {
          ...prev.parents,
          analytics: [],
          loading: false,
          error: errorMessage
        },
        children: {
          ...prev.children,
          analytics: [],
          loading: false,
          error: errorMessage
        }
      }));
    } finally {
      setLoadingAllAnalytics(false);
    }
  }, [filters]);

  // Загрузка данных опроса (вопросы и суммарная информация) - УПРОЩЕННАЯ ВЕРСИЯ
  const loadPollData = useCallback(async (pollType) => {
    const pollId = pollType === 'parents' 
      ? PRESET_POLLS[0].id 
      : PRESET_POLLS[1].id;
    
    // Проверяем, нужно ли загружать данные
    if (pollData[pollType].questions.length > 0) {
      return; // Уже загружены
    }
    
    try {
      setPollData(prev => ({
        ...prev,
        [pollType]: {
          ...prev[pollType],
          loading: true
        }
      }));
      
      // Загружаем БЕЗ фильтров для получения общего количества
      console.log(`Загрузка базовых данных опроса ${pollType}...`);
      
      const response = await api.get(`/api/polls/${pollId}/basic-info`);
      
      if (response.data) {
        const questions = response.data.poll?.questions || [];
        const summary = response.data.summary || { total_responses: 0, filtered_responses: 0 };
        
        setPollData(prev => ({
          ...prev,
          [pollType]: {
            ...prev[pollType],
            questions: questions,
            summary: {
              total_responses: summary.total_responses || 0,
              filtered_responses: prev[pollType].summary.filtered_responses || 0
            },
            loading: false
          }
        }));
      }
    } catch (err) {
      console.error(`Ошибка загрузки данных опроса ${pollType}:`, err);
      setPollData(prev => ({
        ...prev,
        [pollType]: {
          ...prev[pollType],
          loading: false,
          error: `Ошибка загрузки данных опроса: ${err.message || 'Проверьте подключение'}`
        }
      }));
    }
  }, []); // Убрана зависимость от pollData чтобы избежать циклов

  // Обработчик изменения фильтров
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      if (key === 'region') {
        newFilters.district = '';
        newFilters.foodBlockId = '';
      } else if (key === 'district') {
        newFilters.foodBlockId = '';
      }
      
      // Сбрасываем аналитику при изменении фильтров
      setPollData(prevData => ({
        parents: {
          ...prevData.parents,
          analytics: null,
          summary: {
            ...prevData.parents.summary,
            filtered_responses: 0
          }
        },
        children: {
          ...prevData.children,
          analytics: null,
          summary: {
            ...prevData.children.summary,
            filtered_responses: 0
          }
        }
      }));
      
      return newFilters;
    });
  }, []);

  // Сброс всех фильтров
  const clearFilters = useCallback(() => {
    setFilters({
      region: '',
      district: '',
      foodBlockId: ''
    });
    
    setPollData(prev => ({
      parents: {
        ...prev.parents,
        analytics: null,
        summary: {
          ...prev.parents.summary,
          filtered_responses: 0
        }
      },
      children: {
        ...prev.children,
        analytics: null,
        summary: {
          ...prev.children.summary,
          filtered_responses: 0
        }
      }
    }));
    
    setError('');
  }, []);

  // Экспорт результатов для активного опроса
  const exportResults = useCallback(() => {
    const currentData = pollData[activeTab];
    const currentPoll = PRESET_POLLS.find(p => 
      p.id === (activeTab === 'parents' ? PRESET_POLLS[0].id : PRESET_POLLS[1].id)
    );
    
    if (!currentData.analytics || currentData.analytics.length === 0) {
      alert('Нет данных для экспорта');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      
      const exportData = [];
      
      exportData.push(['ОТЧЕТ ПО РЕЗУЛЬТАТАМ ОПРОСА'], ['']);
      exportData.push(['Название опроса:', currentPoll?.title || 'Без названия']);
      exportData.push(['Тип опроса:', activeTab === 'parents' ? 'Опрос родителей' : 'Опрос детей']);
      exportData.push(['Дата экспорта:', new Date().toLocaleDateString('ru-RU')]);
      exportData.push(['Всего вопросов:', currentData.analytics.length]);
      exportData.push(['Всего ответов (с учетом фильтров):', currentData.summary.filtered_responses || 0]);
      exportData.push(['Всего респондентов (без фильтров):', currentData.summary.total_responses || 0]);
      
      const filtersText = [];
      if (filters.region) filtersText.push(`Регион: ${filters.region}`);
      if (filters.district) filtersText.push(`Район: ${filters.district}`);
      if (filters.foodBlockId) {
        const foodBlock = availableFilters.foodBlocks?.find(fb => fb.id === filters.foodBlockId);
        filtersText.push(`Пищеблок: ${foodBlock?.name || filters.foodBlockId}`);
      }
      
      exportData.push(['Примененные фильтры:', filtersText.join(', ') || 'Все данные']);
      exportData.push(['']);
      exportData.push(['']);

      currentData.analytics.forEach((analytics, index) => {
        const questionNumber = index + 1;
        const totalAnswers = analytics.totalAnswers || 0;
        const questionText = currentData.questions[index + 1]?.text || `Вопрос ${questionNumber}`;
        
        // Рассчитываем среднюю оценку для рейтинговых вопросов
        let averageRating = null;
        const filteredOptions = analytics.options?.filter(opt => opt.value !== '0') || [];
        const ratingPattern = /^[1-5]$|^[1-9]$|^10$|^\d+$/;
        const isRatingQuestion = filteredOptions.every(opt => ratingPattern.test(opt.value?.trim()));
        
        if (isRatingQuestion && filteredOptions.length > 0) {
          const ratingValues = filteredOptions.map(opt => ({
            rating: parseInt(opt.value) || 0,
            count: opt.count || 0
          }));
          
          const totalRating = ratingValues.reduce((sum, item) => sum + (item.rating * item.count), 0);
          const totalCount = ratingValues.reduce((sum, item) => sum + item.count, 0);
          
          if (totalCount > 0) {
            averageRating = (totalRating / totalCount).toFixed(1);
          }
        }

        exportData.push([`ВОПРОС ${questionNumber}: ${questionText}`]);
        exportData.push(['Всего ответов:', totalAnswers]);
        
        // Добавляем информацию о пропусках
        const zeroAnswers = analytics.options?.find(opt => opt.value === '0')?.count || 0;
        if (zeroAnswers > 0) {
          exportData.push(['Пропущено вопрос:', zeroAnswers]);
        }
        
        // Добавляем среднюю оценку для рейтингов
        if (averageRating) {
          exportData.push(['Средняя оценка:', averageRating]);
        }
        
        exportData.push(['']);

        // Фильтруем нулевые ответы для экспорта
        const exportOptions = analytics.options?.filter(opt => opt.value !== '0') || [];
        
        if (exportOptions.length > 0) {
          exportData.push(['Ответ', 'Процент', 'Количество ответов']);
          
          exportOptions.forEach(option => {
            exportData.push([
              option.value || '—',
              option.percentage ? `${option.percentage.toFixed(1)}%` : '0%',
              option.count || 0
            ]);
          });
        } else {
          exportData.push(['Нет данных по вариантам ответов']);
        }

        exportData.push(['']);
        exportData.push(['']);
      });

      const ws = XLSX.utils.aoa_to_sheet(exportData);
      
      const colWidths = [
        { wch: 50 },
        { wch: 15 },
        { wch: 20 }
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Результаты опроса 2025');
      
      const fileName = `результаты_${currentPoll?.title?.replace(/\s+/g, '_') || 'опрос'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      alert('Произошла ошибка при экспорте данных');
    }
  }, [pollData, activeTab, filters, availableFilters.foodBlocks]);

  // Загрузка данных опроса при смене вкладки
  useEffect(() => {
    if (isMountedRef.current) {
      loadPollData(activeTab);
    }
  }, [activeTab, loadPollData]);

  // Получение списка регионов
  const regions = useMemo(() => {
    if (!availableFilters.regions) return [];
    
    let regionsList = [];
    
    if (Array.isArray(availableFilters.regions)) {
      regionsList = availableFilters.regions;
    } else if (typeof availableFilters.regions === 'object') {
      regionsList = Object.entries(availableFilters.regions).map(([key, value]) => {
        if (typeof value === 'object') {
          return { ...value, key };
        } else {
          return { name: key, count: value };
        }
      });
    }
    
    return regionsList
      .filter(region => region && (typeof region === 'object' ? region.name : region))
      .sort((a, b) => {
        const nameA = typeof a === 'object' ? a.name || a : a;
        const nameB = typeof b === 'object' ? b.name || b : b;
        return String(nameA).localeCompare(String(nameB));
      });
  }, [availableFilters.regions]);

  // Получение списка районов
  const districts = useMemo(() => {
    if (!filters.region || !availableFilters.districtsByRegion) {
      return [];
    }
    
    const regionDistricts = availableFilters.districtsByRegion[filters.region];
    
    if (!regionDistricts) return [];
    
    let districtsList = [];
    
    if (Array.isArray(regionDistricts)) {
      districtsList = regionDistricts;
    } else if (typeof regionDistricts === 'object') {
      districtsList = Object.values(regionDistricts);
    }
    
    return districtsList
      .filter(district => district && (typeof district === 'object' ? district.name : district))
      .sort((a, b) => {
        const nameA = typeof a === 'object' ? a.name || a : a;
        const nameB = typeof b === 'object' ? b.name || b : b;
        return String(nameA).localeCompare(String(nameB));
      });
  }, [filters.region, availableFilters.districtsByRegion]);

  // Получение списка пищеблоков
  const foodBlocks = useMemo(() => {
    if (!availableFilters.foodBlocks) return [];
    
    let allFoodBlocks = [];
    
    if (Array.isArray(availableFilters.foodBlocks)) {
      allFoodBlocks = availableFilters.foodBlocks;
    } else if (typeof availableFilters.foodBlocks === 'object') {
      allFoodBlocks = Object.values(availableFilters.foodBlocks);
    }
    
    return allFoodBlocks
      .filter(fb => {
        if (!fb || !fb.id) return false;
        
        const matchesRegion = !filters.region || fb.region === filters.region;
        const matchesDistrict = !filters.district || fb.district === filters.district;
        return matchesRegion && matchesDistrict;
      })
      .sort((a, b) => {
        const nameA = a.name || a.id;
        const nameB = b.name || b.id;
        return String(nameA).localeCompare(String(nameB));
      });
  }, [filters.region, filters.district, availableFilters.foodBlocks]);

  const currentPollData = pollData[activeTab];
  const otherPollData = pollData[activeTab === 'parents' ? 'children' : 'parents'];

  // Функция для форматирования числа с разделителями тысяч
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  return (
    <div className="analytics-app">
      {/* Шапка приложения */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-brand">
            <div className="brand-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="white"/>
              </svg>
            </div>
            <div>
              <h1 className="app-title">Аналитика опросов</h1>
              <p className="app-subtitle">Визуализация и анализ результатов опросов родителей и детей</p>
            </div>
          </div>
          <div className="header-actions">
            <div className="poll-badge">
              <span className="badge-label">Текущий опрос:</span>
              <span className="badge-value">
                {activeTab === 'parents' ? 'Опрос родителей' : 'Опрос детей'}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        {/* Панель фильтров */}
        <div className="filters-panel">
          <div className="panel-header">
            <h5 className="panel-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="title-icon">
                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" fill="#0f6cbd"/>
              </svg>
              Фильтры для анализа
            </h5>
            <button 
              onClick={clearFilters} 
              className="btn btn-outline btn-sm"
              disabled={!filters.region && !filters.district && !filters.foodBlockId}
            >
              Сбросить все
            </button>
          </div>

          {error && (
            <div className="error-state" style={{ marginBottom: '20px' }}>
              <div className="error-icon">⚠️</div>
              <div className="error-content">
                <h3 className="error-title">Ошибка загрузки фильтров</h3>
                <p className="error-message">{error}</p>
              </div>
            </div>
          )}

          <div className="filters-grid">
            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">🌍</span>
                Регион
                {loadingFilters && <span className="loading-indicator"> (загрузка...)</span>}
              </label>
              <div className="select-wrapper">
                <select 
                  value={filters.region} 
                  onChange={(e) => handleFilterChange('region', e.target.value)}
                  className="form-select"
                  disabled={loadingFilters}
                >
                  <option value="">— Все регионы —</option>
                  {regions.map((region, index) => {
                    const regionName = typeof region === 'object' ? region.name : region;
                    const regionCount = typeof region === 'object' ? region.count : 0;
                    return (
                      <option key={index} value={regionName}>
                        {regionName} {regionCount > 0 && `(${formatNumber(regionCount)})`}
                      </option>
                    );
                  })}
                </select>
              </div>
              {!loadingFilters && regions.length === 0 && (
                <div className="select-hint">Нет данных по регионам</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">📍</span>
                Район
              </label>
              <div className="select-wrapper">
                <select 
                  value={filters.district} 
                  onChange={(e) => handleFilterChange('district', e.target.value)}
                  className="form-select"
                  disabled={!filters.region || loadingFilters}
                >
                  <option value="">— Все районы —</option>
                  {districts.map((district, index) => {
                    const districtName = typeof district === 'object' ? district.name : district;
                    const districtCount = typeof district === 'object' ? district.count : 0;
                    return (
                      <option key={index} value={districtName}>
                        {districtName} {districtCount > 0 && `(${formatNumber(districtCount)})`}
                      </option>
                    );
                  })}
                </select>
              </div>
              {!filters.region && (
                <div className="select-hint">Выберите регион</div>
              )}
              {filters.region && districts.length === 0 && !loadingFilters && (
                <div className="select-hint">Нет данных по районам</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">🏢</span>
                Пищеблок
              </label>
              <div className="select-wrapper">
                <select 
                  value={filters.foodBlockId} 
                  onChange={(e) => handleFilterChange('foodBlockId', e.target.value)}
                  className="form-select"
                  disabled={!filters.district || loadingFilters}
                >
                  <option value="">— Все пищеблоки —</option>
                  {foodBlocks.map((fb, index) => (
                    <option key={index} value={fb.id}>
                      {fb.name || fb.id} {fb.count > 0 && `(${formatNumber(fb.count)})`}
                    </option>
                  ))}
                </select>
              </div>
              {!filters.district && filters.region && (
                <div className="select-hint">Выберите район</div>
              )}
              {filters.district && foodBlocks.length === 0 && !loadingFilters && (
                <div className="select-hint">Нет данных по пищеблокам</div>
              )}
            </div>
          </div>

          {/* Активные фильтры */}
          {(filters.region || filters.district || filters.foodBlockId) && (
            <div className="active-filters">
              <div className="active-label">Применены фильтры:</div>
              <div className="filter-chips">
                {filters.region && (
                  <span className="filter-chip">
                    <span className="chip-icon">🌍</span>
                    <span className="chip-text">{textHelpers.truncateText(filters.region, 30)}</span>
                  </span>
                )}
                {filters.district && (
                  <span className="filter-chip">
                    <span className="chip-icon">📍</span>
                    <span className="chip-text">{textHelpers.truncateText(filters.district, 30)}</span>
                  </span>
                )}
                {filters.foodBlockId && (
                  <span className="filter-chip">
                    <span className="chip-icon">🏢</span>
                    <span className="chip-text">{textHelpers.truncateText(foodBlocks.find(fb => fb.id === filters.foodBlockId)?.name || filters.foodBlockId, 30)}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>


<div className="poll-tabs">
  <div className="tabs-header">
    <h2 className="tabs-title">Результаты опросов 2025</h2>
  </div>
  <div className="tabs-container">
    <button 
      className={`tab-btn ${activeTab === 'parents' ? 'active' : ''}`}
      onClick={() => setActiveTab('parents')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="tab-icon">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" 
          fill={activeTab === 'parents' ? "white" : "#0f6cbd"}/>
      </svg>
      Опрос родителей
      {pollData.parents.summary.filtered_responses > 0 && (
        <span className="tab-badge">{formatNumber(pollData.parents.summary.filtered_responses)}</span>
      )}
    </button>
    <button 
      className={`tab-btn ${activeTab === 'children' ? 'active' : ''}`}
      onClick={() => setActiveTab('children')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="tab-icon">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" 
          fill={activeTab === 'children' ? "white" : "#0f6cbd"}/>
      </svg>
      Опрос детей
      {pollData.children.summary.filtered_responses > 0 && (
        <span className="tab-badge">{formatNumber(pollData.children.summary.filtered_responses)}</span>
      )}
    </button>
  </div>
</div>

        {/* Панель управления */}
        <div className="control-panel">
          <div className="control-content">
            <div className="control-info">
              <h3 className="control-title">
                {activeTab === 'parents' ? 'Опрос родителей' : 'Опрос детей'}
              </h3>
              {currentPollData.summary.filtered_responses > 0 && (
                <div className="response-summary">
                  <span className="summary-text">Всего ответов (с учетом фильтров):</span>
                  <span className="summary-number">{formatNumber(currentPollData.summary.filtered_responses)}</span>
                  {currentPollData.summary.total_responses > 0 && 
                   currentPollData.summary.filtered_responses !== currentPollData.summary.total_responses && (
                    <span className="summary-total">
                      (всего респондентов: <span className="total-number">{formatNumber(currentPollData.summary.total_responses)}</span>)
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="control-buttons">
              <button 
                onClick={loadAllQuestionAnalytics}
                disabled={loadingAllAnalytics}
                className="btn btn-primary"
              >
                {loadingAllAnalytics ? (
                  <>
                    <span className="btn-spinner"></span>
                    <span>Загрузка всех опросов...</span>
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="btn-icon">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="white"/>
                    </svg>
                    <span>Показать результаты всех опросов</span>
                  </>
                )}
              </button>
              
              {currentPollData.analytics && currentPollData.analytics.length > 0 && (
                <button 
                  onClick={exportResults}
                  className="btn btn-success"
                  disabled={loadingAllAnalytics}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="btn-icon">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="white"/>
                  </svg>
                  <span>Экспорт в Excel</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Статус загрузки всех опросов */}
        {loadingAllAnalytics && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p className="loading-text">Загрузка данных для обоих опросов...</p>
          </div>
        )}

        {/* Состояния загрузки и ошибок для текущего опроса */}
        {currentPollData.error && !loadingAllAnalytics && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <div className="error-content">
              <h3 className="error-title">Ошибка загрузки</h3>
              <p className="error-message">{currentPollData.error}</p>
            </div>
          </div>
        )}

        {/* Результаты */}
        {currentPollData.analytics && currentPollData.analytics.length > 0 && !loadingAllAnalytics ? (
          <div className="results-section">
            <div className="results-header">
              <h2 className="results-title">
                Результаты анализа - {activeTab === 'parents' ? 'Опрос родителей' : 'Опрос детей'}
              </h2>
              <div className="results-meta">
                <span className="meta-text">Вопросов: {currentPollData.analytics.length}</span>
                <span className="meta-divider">•</span>
                <span className="meta-text">Ответов (с учетом фильтров): {formatNumber(currentPollData.summary.filtered_responses)}</span>
                {otherPollData.analytics && otherPollData.analytics.length > 0 && (
                  <>
                    <span className="meta-divider">•</span>
                    <span className="meta-text" style={{color: '#20c997', fontWeight: 500}}>
                      {activeTab === 'parents' ? 'Детский опрос' : 'Родительский опрос'} загружен ({formatNumber(otherPollData.summary.filtered_responses)} ответов)
                    </span>
                  </>
                )}
              </div>
            </div>
            
            <div className="questions-grid">
              {currentPollData.analytics.map((analytics, index) => (
                <QuestionStats
                  key={index}
                  analytics={analytics}
                  questionIndex={index}
                  questionText={currentPollData.questions[index + 1]?.text || ''}
                />
              ))}
            </div>
          </div>
        ) : !loadingAllAnalytics && !currentPollData.error ? (
          <div className="empty-state">
            <div className="empty-content">
              <div className="empty-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="#e9ecef"/>
                </svg>
              </div>
              <h3 className="empty-title">Данные не загружены</h3>
              <p className="empty-text">Нажмите кнопку "Показать результаты всех опросов" для анализа</p>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};